/**
 * @file BonusPoolWorkspace.jsx
 * @module ui/src/components/admin/BonusPoolWorkspace.jsx
 * @description
 *   C-68 — Bonus Pool Workspace. Runtime instrument surface.
 *   EXECUTE → Programs & Adjustments → Bonus Pools.
 *
 *   5 tabs: Overview · Allocations · Results · Approval · Activity
 *
 *   S926 — New component. Backend: p4_bonus_pool_router.py (all 7 routes
 *   live, AD-S924-02). Do NOT build a new bonus pool backend router.
 *
 *   Architecture:
 *   - Standalone runtime instrument — does NOT use usePlanDocument,
 *     usePlanGovernance, or usePlanValidation (design-time hooks).
 *   - No custom hook — direct fetch pattern with mounted ref (OD-S924-B).
 *   - Enterprise tier — feature.tier.bonus_pool gate enforced by §65.1
 *     create only. GET routes have no gate. "New Bonus Pool" button
 *     surfaces API error if tenant lacks entitlement — no client-side
 *     feature flag check.
 *   - No pool_status column in DDL. Approval state derived from
 *     approved_by IS NULL (pending) / IS NOT NULL (approved).
 *   - Two-tier approval:
 *       Tier 1 — Pool approval: §65.3 POST /bonus-pools/{id}/approve
 *         NN-10: created_by ≠ approving user (OD-S926-A — created_by
 *         now returned by §65.2 GET, patched d136e06).
 *         A-10: approved_by IS NOT NULL → already approved (409).
 *         A-16: single write stamps submitted_* AND approved_* AND
 *         updated_at in one UPDATE (backend-only, no UI impact).
 *       Tier 2 — Allocation approval: §65.6 POST /bonus-pool-allocations/{id}/approve
 *         NN-10: nominated_by ≠ approving user.
 *         A-10: is_approved = TRUE → already approved (409).
 *         nomination_approval_required flag from parameter_config:
 *         effect is visible via is_approved on returned allocation rows —
 *         UI shows Approve button only when is_approved=false. No
 *         separate parameter_config fetch needed.
 *   - Pool must be approved before allocations can be created (§65.5
 *     checks approved_by IS NOT NULL → 422 if pool not yet approved).
 *   - Dual-writer: allocation_source='engine' rows are read-only in UI.
 *     Only manager_nomination allocations accept Approve action.
 *   - custom eligibility_type blocked by router (422) — not offered in UI.
 *   - §65.7 Results: calculation_run_id is required query param.
 *     UI provides text input — same pattern as ContestResultsPanel.
 *   - Activity: entity_type='bonus_pool', entity_id=selectedPoolId (uuid).
 *     §30.1 GET /audit-logs with cursor pagination.
 *   - OD-S926-A: Approval tab always shows created_by, submitted_by,
 *     submitted_at, approved_by, approved_at. Never hidden. Audit trail
 *     always visible in Activity tab.
 *   - AD-119: No plan_component_id — scoped tenant + fiscal_period_id.
 *   - Inline panels per OD-S924-B. Refactor when any panel exceeds ~150L.
 *
 *   Routes wired (p4_bonus_pool_router.py):
 *   §65.1  POST   /bonus-pools                          — create pool
 *   §65.2  GET    /bonus-pools                          — list pools
 *   §65.3  POST   /bonus-pools/{id}/approve             — approve pool
 *   §65.4  GET    /bonus-pool-allocations               — list allocations
 *   §65.5  POST   /bonus-pool-allocations               — nominate allocation
 *   §65.6  POST   /bonus-pool-allocations/{id}/approve  — approve allocation
 *   §65.7  GET    /bonus-pool-results                   — results (engine)
 *
 *   Activity:
 *   §30.1  GET    /audit-logs  entity_type=bonus_pool entity_id=<uuid>
 *
 *   CSS: index.css + tokens.css. All colours via rgb(var(--color-*)).
 *   Button classes: .primary-button .secondary-button .icon-button .text-button
 *   Table classes: .modifier-manager-table-wrapper .modifier-manager-table
 *   Badge classes: .modifier-manager-badge--*
 *
 *   UI directives (carry from S924/S925):
 *   D-1: Tables → scrollable + sortable + searchable + clickable drill-down
 *   D-2: Long screens → collapsible sections (secondary collapsed by default)
 *
 * @author data-fit LLC · DecisionFlow · S926
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const API_BASE = '/api/v1';

// ---------------------------------------------------------------------------
// Constants — all values confirmed from p4_bonus_pool_router.py source
// ---------------------------------------------------------------------------

const TABS = [
  { key: 'overview',     label: 'Overview' },
  { key: 'allocations',  label: 'Allocations' },
  { key: 'results',      label: 'Results' },
  { key: 'approval',     label: 'Approval' },
  { key: 'activity',     label: 'Activity' },
];

// allocation_method CHECK confirmed from DDL r64 and router _VALID_ALLOCATION_METHODS
const ALLOCATION_METHODS = ['by_rank', 'by_score', 'manager_nomination', 'equal_share'];
const ALLOCATION_METHOD_LABELS = {
  by_rank:            'By Rank',
  by_score:           'By Score',
  manager_nomination: 'Manager Nomination',
  equal_share:        'Equal Share',
};

// eligibility_type CHECK confirmed from DDL r64 and router _VALID_ELIGIBILITY_TYPES
// 'custom' excluded — router returns 422 (BONUS_POOL_CUSTOM_ELIGIBILITY_NOT_IMPLEMENTED)
const ELIGIBILITY_TYPES = ['pod', 'role', 'segment', 'business_unit'];
const ELIGIBILITY_LABELS = {
  pod:           'Pod',
  role:          'Role',
  segment:       'Segment',
  business_unit: 'Business Unit',
};

// Approval state derived from approved_by (no pool_status column in DDL)
const POOL_APPROVAL_BADGE = (pool) =>
  pool?.approved_by
    ? { cls: 'modifier-manager-badge--complete', label: 'Approved' }
    : { cls: 'modifier-manager-badge--warning',  label: 'Pending Approval' };

// ---------------------------------------------------------------------------
// Shared helpers (follow ContestWorkspace.jsx pattern exactly)
// ---------------------------------------------------------------------------

function _iso(v) {
  if (!v) return '—';
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}

function _fmt(amount) {
  if (amount == null) return '—';
  const num = parseFloat(amount);
  if (isNaN(num)) return String(amount);
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function _pct(value) {
  if (value == null) return '—';
  const num = parseFloat(value);
  if (isNaN(num)) return String(value);
  return `${(num * 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}%`;
}

async function apiFetch(url, opts = {}) {
  const r = await fetch(url, { credentials: 'include', ...opts });
  if (!r.ok) {
    const b = await r.json().catch(() => ({}));
    const err = new Error(b?.error?.message || b?.detail || `Request failed (${r.status})`);
    err.status = r.status;
    err.errorCode = b?.error?.code ?? null;
    throw err;
  }
  return r.json();
}

// ---------------------------------------------------------------------------
// S928 — OI-S920-403-APPROVAL-ERROR-NOT-SURFACED
// Shared SoD 403 error classifier for all approval actions in this workspace.
// Returns an actionable message string; caller sets its own error state.
// ---------------------------------------------------------------------------
function handleApprovalError(err) {
  const is403 = err.status === 403;
  const isSoD = is403
    || (err.errorCode && err.errorCode.toLowerCase().includes('segregation'))
    || (err.message  && (
         err.message.toLowerCase().includes('segregation') ||
         err.message.toLowerCase().includes('cannot approve') ||
         err.message.toLowerCase().includes('created this') ||
         err.message.toLowerCase().includes('sod') ||
         err.message.toLowerCase().includes('separation of duties')
       ));
  if (isSoD) {
    return 'You cannot approve this — Separation of Duties (NN-10) prevents the creator from approving their own record. A different comp admin must perform this action.';
  }
  return err.message || 'An unexpected error occurred.';
}

// ---------------------------------------------------------------------------
// Shared primitives (follow ContestWorkspace.jsx pattern exactly)
// ---------------------------------------------------------------------------

function Field({ label, hint, error, children, required }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {label && (
        <label style={{ fontSize: 13, fontWeight: 600, color: 'rgb(var(--color-text))' }}>
          {label}{required && <span style={{ color: 'rgb(var(--color-danger))' }}> *</span>}
        </label>
      )}
      {children}
      {hint && !error && <span style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))', marginTop: 2 }}>{hint}</span>}
      {error && <span style={{ fontSize: 12, color: 'rgb(var(--color-danger))', marginTop: 2 }} role="alert">{error}</span>}
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={{ display: 'flex', gap: 12, padding: '6px 0', borderBottom: '1px solid rgb(var(--color-border))' }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: 'rgb(var(--color-text-muted))', minWidth: 200 }}>{label}</span>
      <span style={{ fontSize: 13, color: 'rgb(var(--color-text))' }}>{value ?? '—'}</span>
    </div>
  );
}

function CollapsibleSection({ title, defaultOpen = true, badge, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 10, background: 'rgb(var(--color-surface))', overflow: 'hidden' }}>
      <button type="button" onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer', borderBottom: open ? '1px solid rgb(var(--color-border))' : 'none' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'rgb(var(--color-text))' }}>{title}</span>
          {badge && <span className={`modifier-manager-badge ${badge.cls}`}>{badge.label}</span>}
        </div>
        <span style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && <div style={{ padding: 16 }}>{children}</div>}
    </div>
  );
}

const S = {
  input: {
    border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '6px 10px',
    fontSize: 13, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))',
    width: '100%', boxSizing: 'border-box',
  },
  select: {
    border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '6px 10px',
    fontSize: 13, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))',
    width: '100%', boxSizing: 'border-box',
  },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  grid3: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 },
};

// ===========================================================================
// TAB 1 — OVERVIEW
// §65.2 GET /bonus-pools (list — includes created_by per d136e06 patch)
// §65.1 POST /bonus-pools (create — enterprise gate enforced by router)
// Pool row click → sets selectedPoolId, switches to Allocations tab
// ===========================================================================
function OverviewPanel({ fiscalPeriodId, onSelectPool, selectedPoolId }) {
  const [pools, setPools]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [search, setSearch]       = useState('');

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({
    pool_name: '', pool_code: '', fiscal_period_id: fiscalPeriodId ?? '',
    pool_amount: '', pool_currency_code: 'USD',
    allocation_method: 'manager_nomination', eligibility_type: 'role',
    eligibility_role_id: '', eligibility_pod_id: '',
    eligibility_segment_id: '', eligibility_business_unit_id: '',
  });
  const [creating, setCreating]   = useState(false);
  const [createError, setCreateError] = useState(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const loadPools = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ limit: '200' });
      if (fiscalPeriodId) params.set('fiscal_period_id', String(fiscalPeriodId));
      const d = await apiFetch(`${API_BASE}/bonus-pools?${params}`);
      if (mountedRef.current) setPools(d.items ?? []);
    } catch (e) {
      if (mountedRef.current) setError(e.message);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [fiscalPeriodId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadPools(); }, [loadPools]);

  function resetCreateForm() {
    setCreateForm({
      pool_name: '', pool_code: '', fiscal_period_id: fiscalPeriodId ?? '',
      pool_amount: '', pool_currency_code: 'USD',
      allocation_method: 'manager_nomination', eligibility_type: 'role',
      eligibility_role_id: '', eligibility_pod_id: '',
      eligibility_segment_id: '', eligibility_business_unit_id: '',
    });
    setCreateError(null);
  }

  async function handleCreate() {
    setCreating(true); setCreateError(null);
    try {
      const body = {
        pool_name:          createForm.pool_name,
        pool_code:          createForm.pool_code,
        fiscal_period_id:   parseInt(createForm.fiscal_period_id, 10),
        pool_amount:        createForm.pool_amount,
        pool_currency_code: createForm.pool_currency_code,
        allocation_method:  createForm.allocation_method,
        eligibility_type:   createForm.eligibility_type,
      };
      // Conditional eligibility FK — only include the relevant one
      if (createForm.eligibility_type === 'role' && createForm.eligibility_role_id)
        body.eligibility_role_id = parseInt(createForm.eligibility_role_id, 10);
      if (createForm.eligibility_type === 'pod' && createForm.eligibility_pod_id)
        body.eligibility_pod_id = createForm.eligibility_pod_id;
      if (createForm.eligibility_type === 'segment' && createForm.eligibility_segment_id)
        body.eligibility_segment_id = parseInt(createForm.eligibility_segment_id, 10);
      if (createForm.eligibility_type === 'business_unit' && createForm.eligibility_business_unit_id)
        body.eligibility_business_unit_id = parseInt(createForm.eligibility_business_unit_id, 10);

      const result = await apiFetch(`${API_BASE}/bonus-pools`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      resetCreateForm();
      setShowCreate(false);
      setRefreshKey(k => k + 1);
      // Auto-select the newly created pool
      onSelectPool?.(result.bonus_pool_id, pools);
    } catch (e) {
      setCreateError(e.message);
    } finally {
      setCreating(false);
    }
  }

  const filtered = pools.filter(p =>
    !search ||
    p.pool_name?.toLowerCase().includes(search.toLowerCase()) ||
    p.pool_code?.toLowerCase().includes(search.toLowerCase())
  );

  const pendingCount = pools.filter(p => !p.approved_by).length;
  const createValid  = createForm.pool_name && createForm.pool_code &&
    createForm.fiscal_period_id && createForm.pool_amount && createForm.pool_currency_code;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Summary strip */}
      {pendingCount > 0 && (
        <div style={{ padding: '8px 14px', borderRadius: 8, background: 'rgb(var(--color-warning) / 0.08)', border: '1px solid rgb(var(--color-warning) / 0.25)', fontSize: 12 }}>
          <strong>{pendingCount}</strong> pool{pendingCount !== 1 ? 's' : ''} awaiting approval.
        </div>
      )}

      {/* Filter bar */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <input type="text" placeholder="Search pools…" value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '5px 10px', fontSize: 12, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))' }} />
        <button type="button" onClick={() => setRefreshKey(k => k + 1)}
          style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))', background: 'none', border: 'none', cursor: 'pointer' }}>
          ↻ Refresh
        </button>
        <button type="button" className="primary-button" style={{ marginLeft: 'auto' }}
          onClick={() => { resetCreateForm(); setShowCreate(true); }}>
          + New Bonus Pool
        </button>
      </div>

      {/* Row count */}
      <div style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>
        {loading ? 'Loading…' : `${filtered.length} of ${pools.length} pool${pools.length !== 1 ? 's' : ''}`}
        {fiscalPeriodId && ` · Period ${fiscalPeriodId}`}
        <span style={{ marginLeft: 8, color: 'rgb(var(--color-text-muted))' }}>
          · AD-119: scoped at tenant + fiscal period, no plan component
        </span>
      </div>

      {error && <div style={{ padding: '12px 16px', color: 'rgb(var(--color-danger))', fontSize: 13 }} role="alert">{error}</div>}

      {/* Pools table — D-1 */}
      {!loading && filtered.length === 0 && !showCreate && (
        <div className="modifier-manager-empty-state">
          <h4>No bonus pools</h4>
          <p>Create the first bonus pool for this period. Enterprise feature — tenant must have bonus pool entitlement.</p>
          <button type="button" className="primary-button" onClick={() => { resetCreateForm(); setShowCreate(true); }}>
            + New Bonus Pool
          </button>
        </div>
      )}

      {filtered.length > 0 && (
        <div className="modifier-manager-table-wrapper">
          <table className="modifier-manager-table">
            <thead>
              <tr>
                <th>Pool</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Eligibility</th>
                <th>Approval</th>
                <th>Created</th>
                <th style={{ width: 60 }} />
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => {
                const badge = POOL_APPROVAL_BADGE(p);
                const isSelected = p.bonus_pool_id === selectedPoolId;
                return (
                  <tr key={p.bonus_pool_id}
                    onClick={() => onSelectPool?.(p.bonus_pool_id, pools)}
                    style={{ cursor: 'pointer', background: isSelected ? 'rgb(var(--color-primary) / 0.05)' : undefined }}>
                    <td>
                      <div style={{ fontSize: 13, fontWeight: 600, color: 'rgb(var(--color-text))' }}>{p.pool_name}</div>
                      <code style={{ fontSize: 11, fontFamily: 'ui-monospace,monospace', background: 'rgb(var(--color-surface-muted))', padding: '1px 5px', borderRadius: 3, border: '1px solid rgb(var(--color-border))' }}>
                        {p.pool_code}
                      </code>
                    </td>
                    <td style={{ fontSize: 13, fontWeight: 600 }}>
                      {_fmt(p.pool_amount)} <span style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>{p.pool_currency_code}</span>
                    </td>
                    <td style={{ fontSize: 12 }}>{ALLOCATION_METHOD_LABELS[p.allocation_method] ?? p.allocation_method}</td>
                    <td style={{ fontSize: 12 }}>{ELIGIBILITY_LABELS[p.eligibility_type] ?? p.eligibility_type}</td>
                    <td><span className={`modifier-manager-badge ${badge.cls}`}>{badge.label}</span></td>
                    <td style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>{_iso(p.created_at)}</td>
                    <td>
                      <button type="button" className="text-button"
                        onClick={e => { e.stopPropagation(); onSelectPool?.(p.bonus_pool_id, pools); }}
                        aria-label={`Open ${p.pool_name}`}>→</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Create form */}
      {showCreate && (
        <CollapsibleSection title="New Bonus Pool" defaultOpen>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            <div style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>
              Enterprise feature — requires <code>feature.tier.bonus_pool</code> entitlement.
              AD-119: no plan component required — scoped at tenant + fiscal period.
            </div>

            <div style={S.grid2}>
              <Field label="Pool name" required>
                <input style={S.input} value={createForm.pool_name}
                  onChange={e => setCreateForm(f => ({ ...f, pool_name: e.target.value }))}
                  placeholder="e.g. Q3 Enterprise Bonus Pool" />
              </Field>
              <Field label="Pool code" required>
                <input style={S.input} value={createForm.pool_code}
                  onChange={e => setCreateForm(f => ({ ...f, pool_code: e.target.value.toUpperCase() }))}
                  placeholder="e.g. Q3_ENT_BONUS" />
              </Field>
            </div>

            <div style={S.grid3}>
              <Field label="Pool amount" required>
                <input type="number" style={S.input} min="0" step="0.01"
                  value={createForm.pool_amount}
                  onChange={e => setCreateForm(f => ({ ...f, pool_amount: e.target.value }))}
                  placeholder="e.g. 50000" />
              </Field>
              <Field label="Currency" required>
                <input style={S.input} value={createForm.pool_currency_code} maxLength={3}
                  onChange={e => setCreateForm(f => ({ ...f, pool_currency_code: e.target.value.toUpperCase() }))}
                  placeholder="USD" />
              </Field>
              <Field label="Fiscal period ID" required>
                <input type="number" style={S.input} value={createForm.fiscal_period_id}
                  onChange={e => setCreateForm(f => ({ ...f, fiscal_period_id: e.target.value }))} />
              </Field>
            </div>

            <div style={S.grid2}>
              <Field label="Allocation method" required>
                <select style={S.select} value={createForm.allocation_method}
                  onChange={e => setCreateForm(f => ({ ...f, allocation_method: e.target.value }))}>
                  {ALLOCATION_METHODS.map(m => <option key={m} value={m}>{ALLOCATION_METHOD_LABELS[m]}</option>)}
                </select>
              </Field>
              <Field label="Eligibility type" required hint="'Custom' not yet implemented">
                <select style={S.select} value={createForm.eligibility_type}
                  onChange={e => setCreateForm(f => ({ ...f, eligibility_type: e.target.value }))}>
                  {ELIGIBILITY_TYPES.map(t => <option key={t} value={t}>{ELIGIBILITY_LABELS[t]}</option>)}
                </select>
              </Field>
            </div>

            {/* Conditional eligibility FK field */}
            {createForm.eligibility_type === 'role' && (
              <Field label="Role ID" hint="Integer role_id to scope eligibility">
                <input type="number" style={S.input} value={createForm.eligibility_role_id}
                  onChange={e => setCreateForm(f => ({ ...f, eligibility_role_id: e.target.value }))} />
              </Field>
            )}
            {createForm.eligibility_type === 'pod' && (
              <Field label="Pod ID (UUID)" hint="Pod UUID to scope eligibility">
                <input style={S.input} value={createForm.eligibility_pod_id}
                  onChange={e => setCreateForm(f => ({ ...f, eligibility_pod_id: e.target.value }))}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
              </Field>
            )}
            {createForm.eligibility_type === 'segment' && (
              <Field label="Segment ID" hint="Integer segment_id to scope eligibility">
                <input type="number" style={S.input} value={createForm.eligibility_segment_id}
                  onChange={e => setCreateForm(f => ({ ...f, eligibility_segment_id: e.target.value }))} />
              </Field>
            )}
            {createForm.eligibility_type === 'business_unit' && (
              <Field label="Business unit ID" hint="Integer business_unit_id to scope eligibility">
                <input type="number" style={S.input} value={createForm.eligibility_business_unit_id}
                  onChange={e => setCreateForm(f => ({ ...f, eligibility_business_unit_id: e.target.value }))} />
              </Field>
            )}

            {createError && (
              <div className="modifier-manager-error-state">
                <p style={{ margin: 0 }}>{createError}</p>
              </div>
            )}

            <div className="modifier-manager-footer">
              <span className="modifier-manager-footer-status" />
              <div className="modifier-manager-footer-actions">
                <button type="button" className="secondary-button"
                  onClick={() => { resetCreateForm(); setShowCreate(false); }}>
                  Cancel
                </button>
                <button type="button" className="primary-button"
                  disabled={creating || !createValid} onClick={handleCreate}>
                  {creating ? 'Creating…' : 'Create bonus pool'}
                </button>
              </div>
            </div>
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
}

// ===========================================================================
// TAB 2 — ALLOCATIONS
// §65.4 GET /bonus-pool-allocations (requires bonus_pool_id)
// §65.5 POST /bonus-pool-allocations (manager_nomination only)
// §65.6 POST /bonus-pool-allocations/{id}/approve (NN-10: nominated_by ≠ user)
// Pool must be approved before allocations can be created (§65.5 enforces)
// Overflow: total_pct > 1.0 OR total_amount > pool_amount → 422 from router
// ===========================================================================
function AllocationsPanel({ selectedPool, currentUser }) {
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [refreshKey, setRefreshKey]   = useState(0);

  // Nominate form
  const [showNominate, setShowNominate] = useState(false);
  const [nomForm, setNomForm] = useState({
    position_id: '', allocation_pct: '', allocation_amount: '', nominated_by: '',
  });
  const [nominating, setNominating]   = useState(false);
  const [nomError, setNomError]       = useState(null);

  // Per-row approve state
  const [approvingId, setApprovingId] = useState(null);
  const [approveError, setApproveError] = useState({});

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const poolId = selectedPool?.bonus_pool_id;
  const poolApproved = !!selectedPool?.approved_by;

  const loadAllocations = useCallback(async () => {
    if (!poolId) return;
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ bonus_pool_id: poolId, limit: '200' });
      const d = await apiFetch(`${API_BASE}/bonus-pool-allocations?${params}`);
      if (mountedRef.current) setAllocations(d.items ?? []);
    } catch (e) {
      if (mountedRef.current) setError(e.message);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [poolId, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadAllocations(); }, [loadAllocations]);

  // Running totals for overflow display
  const totalPct = allocations.reduce((s, a) => s + parseFloat(a.allocation_pct ?? 0), 0);
  const totalAmt = allocations.reduce((s, a) => s + parseFloat(a.allocation_amount ?? 0), 0);
  const poolAmount = parseFloat(selectedPool?.pool_amount ?? 0);
  const remainingAmt = poolAmount - totalAmt;
  const remainingPct = 1.0 - totalPct;

  async function handleNominate() {
    setNominating(true); setNomError(null);
    try {
      const body = {
        bonus_pool_id:     poolId,
        position_id:       nomForm.position_id,
        allocation_pct:    nomForm.allocation_pct,
        allocation_amount: nomForm.allocation_amount,
        nominated_by:      nomForm.nominated_by,
      };
      await apiFetch(`${API_BASE}/bonus-pool-allocations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      setNomForm({ position_id: '', allocation_pct: '', allocation_amount: '', nominated_by: '' });
      setShowNominate(false);
      setRefreshKey(k => k + 1);
    } catch (e) {
      setNomError(e.message);
    } finally {
      setNominating(false);
    }
  }

  async function handleApproveAllocation(allocationId) {
    setApprovingId(allocationId);
    setApproveError(s => ({ ...s, [allocationId]: null }));
    try {
      await apiFetch(`${API_BASE}/bonus-pool-allocations/${allocationId}/approve`, { method: 'POST' });
      setRefreshKey(k => k + 1);
    } catch (e) {
      setApproveError(s => ({ ...s, [allocationId]: handleApprovalError(e) }));
    } finally {
      setApprovingId(null);
    }
  }

  const myUserId = currentUser?.user_id ?? currentUser?.user_account_id;
  const nomValid = nomForm.position_id && nomForm.allocation_pct &&
    nomForm.allocation_amount && nomForm.nominated_by;

  if (!poolId) {
    return (
      <div className="modifier-manager-empty-state">
        <h4>No pool selected</h4>
        <p>Select a bonus pool from the Overview tab to manage allocations.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Pool context header */}
      <CollapsibleSection title="Pool Summary" defaultOpen>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <InfoRow label="Pool name"   value={selectedPool?.pool_name} />
          <InfoRow label="Pool code"   value={selectedPool?.pool_code} />
          <InfoRow label="Total pool"  value={`${_fmt(selectedPool?.pool_amount)} ${selectedPool?.pool_currency_code ?? ''}`} />
          <InfoRow label="Method"      value={ALLOCATION_METHOD_LABELS[selectedPool?.allocation_method] ?? selectedPool?.allocation_method} />
          <InfoRow label="Allocated"   value={`${_fmt(totalAmt)} (${_pct(totalPct)})`} />
          <InfoRow label="Remaining"   value={
            <span style={{ color: remainingAmt < 0 ? 'rgb(var(--color-danger))' : 'rgb(var(--color-success))' }}>
              {_fmt(remainingAmt)} ({_pct(remainingPct)})
              {remainingAmt < 0 && ' ⚠ Over-allocated'}
            </span>
          } />
          <InfoRow label="Pool status" value={
            <span className={`modifier-manager-badge ${POOL_APPROVAL_BADGE(selectedPool).cls}`}>
              {POOL_APPROVAL_BADGE(selectedPool).label}
            </span>
          } />
        </div>
      </CollapsibleSection>

      {/* Gate — pool must be approved before nominations */}
      {!poolApproved && (
        <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgb(var(--color-warning) / 0.08)', border: '1px solid rgb(var(--color-warning) / 0.25)', fontSize: 12, color: 'rgb(var(--color-text))' }}>
          Pool must be approved (Approval tab) before allocations can be nominated.
        </div>
      )}

      {/* Nominate button — only when pool is approved */}
      {poolApproved && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="button" className="primary-button"
            onClick={() => setShowNominate(true)}>
            + Nominate Allocation
          </button>
        </div>
      )}

      {error && <div style={{ padding: '12px 16px', color: 'rgb(var(--color-danger))', fontSize: 13 }} role="alert">{error}</div>}

      {loading && allocations.length === 0 && (
        <div className="modifier-manager-loading">Loading allocations…</div>
      )}

      {!loading && allocations.length === 0 && (
        <div className="modifier-manager-empty-state">
          <h4>No allocations yet</h4>
          <p>{poolApproved ? 'Nominate the first allocation above.' : 'Approve the pool first, then nominate allocations.'}</p>
        </div>
      )}

      {/* Allocations table — D-1 */}
      {allocations.length > 0 && (
        <div className="modifier-manager-table-wrapper">
          <table className="modifier-manager-table">
            <thead>
              <tr>
                <th>Position</th>
                <th>Source</th>
                <th>Pct</th>
                <th>Amount</th>
                <th>Nominated by</th>
                <th>Approved</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {allocations.map(a => {
                const isManagerNom = a.allocation_source === 'manager_nomination';
                const isSelf       = myUserId && a.nominated_by && String(myUserId) === String(a.nominated_by);
                const canApprove   = isManagerNom && !a.is_approved && !isSelf;
                const selfBlocked  = isManagerNom && !a.is_approved && isSelf;
                const rowErr       = approveError[a.allocation_id];

                return (
                  <tr key={a.allocation_id}>
                    <td style={{ fontFamily: 'ui-monospace,monospace', fontSize: 11 }}>
                      {String(a.position_id ?? '').slice(0, 8)}…
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {a.allocation_source === 'engine'
                        ? <span style={{ color: 'rgb(var(--color-text-muted))' }}>Engine</span>
                        : 'Manager Nomination'}
                    </td>
                    <td style={{ fontSize: 12 }}>{_pct(a.allocation_pct)}</td>
                    <td style={{ fontSize: 12, fontWeight: 600 }}>{_fmt(a.allocation_amount)}</td>
                    <td style={{ fontFamily: 'ui-monospace,monospace', fontSize: 11 }}>
                      {String(a.nominated_by ?? '').slice(0, 8)}…
                    </td>
                    <td>
                      {a.is_approved
                        ? <span className="modifier-manager-badge modifier-manager-badge--complete">Approved</span>
                        : <span className="modifier-manager-badge modifier-manager-badge--warning">Pending</span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {canApprove && (
                          <button type="button" className="text-button"
                            disabled={approvingId === a.allocation_id}
                            onClick={() => handleApproveAllocation(a.allocation_id)}>
                            {approvingId === a.allocation_id ? 'Approving…' : 'Approve'}
                          </button>
                        )}
                        {selfBlocked && (
                          <span style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>
                            NN-10: you nominated this
                          </span>
                        )}
                        {a.allocation_source === 'engine' && (
                          <span style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>Engine-written</span>
                        )}
                        {rowErr && (
                          <span style={{ fontSize: 11, color: 'rgb(var(--color-danger))' }} role="alert">{rowErr}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Nominate form */}
      {showNominate && poolApproved && (
        <CollapsibleSection title="Nominate Allocation" defaultOpen>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>
              Allocation source is always <strong>manager_nomination</strong> for API-created rows.
              Total pct and amount must not exceed pool totals — router enforces overflow.
            </div>
            <div style={S.grid2}>
              <Field label="Position ID" required hint="UUID of recipient position">
                <input style={S.input} value={nomForm.position_id}
                  onChange={e => setNomForm(f => ({ ...f, position_id: e.target.value }))}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
              </Field>
              <Field label="Nominated by (user ID)" required
                hint="UUID of nominating user. NN-10: this person cannot approve.">
                <input style={S.input} value={nomForm.nominated_by}
                  onChange={e => setNomForm(f => ({ ...f, nominated_by: e.target.value }))}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
              </Field>
            </div>
            <div style={S.grid2}>
              <Field label="Allocation %" required hint="Decimal — e.g. 0.1500 for 15%">
                <input type="number" style={S.input} min="0" max="1" step="0.0001"
                  value={nomForm.allocation_pct}
                  onChange={e => setNomForm(f => ({ ...f, allocation_pct: e.target.value }))}
                  placeholder="e.g. 0.1500" />
              </Field>
              <Field label="Allocation amount" required
                hint={`Remaining: ${_fmt(remainingAmt)} ${selectedPool?.pool_currency_code ?? ''}`}>
                <input type="number" style={S.input} min="0" step="0.01"
                  value={nomForm.allocation_amount}
                  onChange={e => setNomForm(f => ({ ...f, allocation_amount: e.target.value }))} />
              </Field>
            </div>

            {nomError && (
              <div className="modifier-manager-error-state">
                <p style={{ margin: 0 }}>{nomError}</p>
              </div>
            )}

            <div className="modifier-manager-footer">
              <span className="modifier-manager-footer-status" />
              <div className="modifier-manager-footer-actions">
                <button type="button" className="secondary-button"
                  onClick={() => { setShowNominate(false); setNomError(null); }}>
                  Cancel
                </button>
                <button type="button" className="primary-button"
                  disabled={nominating || !nomValid} onClick={handleNominate}>
                  {nominating ? 'Nominating…' : 'Nominate allocation'}
                </button>
              </div>
            </div>
          </div>
        </CollapsibleSection>
      )}
    </div>
  );
}

// ===========================================================================
// TAB 3 — RESULTS
// §65.7 GET /bonus-pool-results (calculation_run_id required)
// Fields returned (from router keys lines 705–707, confirmed):
// pool_result_id, bonus_pool_id, position_id, calculation_run_id,
// pool_amount_snapshot, allocation_pct, payout_amount, fiscal_period_id, created_at
// payout_currency_code and pool_allocation_id in DDL but NOT in router keys — not displayed
// Pattern: same as ContestResultsPanel (calculation_run_id UUID text input)
// ===========================================================================
function ResultsPanel({ selectedPool }) {
  const [runId, setRunId]     = useState('');
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const poolId = selectedPool?.bonus_pool_id;

  async function loadResults() {
    if (!runId.trim() || !poolId) return;
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({
        calculation_run_id: runId.trim(),
        bonus_pool_id:      poolId,
        limit:              '200',
      });
      const d = await apiFetch(`${API_BASE}/bonus-pool-results?${params}`);
      setRows(d.items ?? []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  if (!poolId) {
    return (
      <div className="modifier-manager-empty-state">
        <h4>No pool selected</h4>
        <p>Select a bonus pool from the Overview tab to view results.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      <div style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))' }}>
        Results are engine-written and read-only. Enter a final calculation run ID to view payouts.
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
        <Field label="Calculation run ID" hint="UUID from a completed final calculation run">
          <input type="text" style={{ ...S.input, width: 320 }} value={runId}
            onChange={e => setRunId(e.target.value)}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
        </Field>
        <button type="button" className="primary-button"
          onClick={loadResults} disabled={loading || !runId.trim() || !poolId}>
          {loading ? 'Loading…' : 'Load results'}
        </button>
      </div>

      {error && <div className="modifier-manager-error-state"><p style={{ margin: 0 }}>{error}</p></div>}

      {rows.length === 0 && !loading && !error && (
        <div className="modifier-manager-empty-state">
          <h4>No results</h4>
          <p>Enter a calculation run ID and click Load results. Results appear after a final calculation run that includes this bonus pool.</p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="modifier-manager-table-wrapper">
          <table className="modifier-manager-table">
            <thead>
              <tr>
                <th>Position</th>
                <th>Pool snapshot</th>
                <th>Allocation %</th>
                <th>Payout</th>
                <th>Period</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.pool_result_id}>
                  <td style={{ fontFamily: 'ui-monospace,monospace', fontSize: 11 }}>
                    {String(r.position_id ?? '').slice(0, 8)}…
                  </td>
                  <td style={{ fontSize: 12 }}>{_fmt(r.pool_amount_snapshot)}</td>
                  <td style={{ fontSize: 12 }}>{_pct(r.allocation_pct)}</td>
                  <td style={{ fontSize: 13, fontWeight: 600 }}>{_fmt(r.payout_amount)}</td>
                  <td style={{ fontSize: 12 }}>{r.fiscal_period_id}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))', padding: '8px 0' }}>
            {rows.length} positions · Engine-written, read-only (§65.7)
          </div>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// TAB 4 — APPROVAL
// §65.3 POST /bonus-pools/{id}/approve
// OD-S926-A: full actor transparency — created_by, submitted_by, submitted_at,
// approved_by, approved_at always shown. created_by now in §65.2 response
// (patched d136e06). NN-10: created_by ≠ approving user.
// A-10: approved_by IS NOT NULL → already approved (409).
// A-16: single write stamps submitted_* AND approved_* (backend-only).
// ===========================================================================
function ApprovalPanel({ selectedPool, currentUser, onActionComplete }) {
  const [approving, setApproving]     = useState(false);
  const [approveError, setApproveError] = useState(null);

  const pool     = selectedPool;
  const myUserId = currentUser?.user_id ?? currentUser?.user_account_id;

  // NN-10 pre-check: created_by ≠ approving user
  // created_by now returned by §65.2 (OD-S926-A, d136e06 patch)
  const isSelf = myUserId && pool?.created_by
    ? String(myUserId) === String(pool.created_by)
    : false;

  const isApproved = !!pool?.approved_by;

  async function handleApprove() {
    if (!pool?.bonus_pool_id) return;
    setApproving(true); setApproveError(null);
    try {
      await apiFetch(`${API_BASE}/bonus-pools/${pool.bonus_pool_id}/approve`, { method: 'POST' });
      onActionComplete?.();
    } catch (e) {
      setApproveError(handleApprovalError(e));
    } finally {
      setApproving(false);
    }
  }

  if (!pool) {
    return (
      <div className="modifier-manager-empty-state">
        <h4>No pool selected</h4>
        <p>Select a bonus pool from the Overview tab to manage approval.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* OD-S926-A: Approval actor transparency — always visible, never collapsed */}
      <CollapsibleSection title="Approval Status" defaultOpen>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          <InfoRow label="Pool"
            value={<><strong>{pool.pool_name}</strong> <code style={{ fontSize: 11 }}>{pool.pool_code}</code></>} />
          <InfoRow label="Created by"
            value={pool.created_by
              ? <code style={{ fontSize: 12, fontFamily: 'ui-monospace,monospace' }}>{String(pool.created_by).slice(0, 8)}…</code>
              : '—'} />
          <InfoRow label="Submitted by"
            value={pool.submitted_by
              ? <code style={{ fontSize: 12, fontFamily: 'ui-monospace,monospace' }}>{String(pool.submitted_by).slice(0, 8)}…</code>
              : '—'} />
          <InfoRow label="Submitted at"  value={_iso(pool.submitted_at)} />
          <InfoRow label="Approved by"
            value={pool.approved_by
              ? <code style={{ fontSize: 12, fontFamily: 'ui-monospace,monospace' }}>{String(pool.approved_by).slice(0, 8)}…</code>
              : '—'} />
          <InfoRow label="Approved at"  value={_iso(pool.approved_at)} />
          <InfoRow label="Current state"
            value={<span className={`modifier-manager-badge ${POOL_APPROVAL_BADGE(pool).cls}`}>{POOL_APPROVAL_BADGE(pool).label}</span>} />
        </div>
      </CollapsibleSection>

      {/* SoD rules */}
      <CollapsibleSection title="Separation of Duties" defaultOpen={false}>
        <div style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))', lineHeight: 1.6 }}>
          <p style={{ margin: '0 0 8px' }}>
            NN-10: The user who created this pool cannot approve it.
            A different comp admin must perform the approval.
          </p>
          <p style={{ margin: '0 0 8px' }}>
            A-10: Double-approve is blocked — approving an already-approved pool returns 409.
          </p>
          <p style={{ margin: 0 }}>
            A-16: Approval stamps submitted_by, submitted_at, approved_by, approved_at
            and updated_at in a single atomic write.
          </p>
        </div>
      </CollapsibleSection>

      {/* Action area */}
      {!isApproved && (
        <div style={{ padding: 16, border: '1px solid rgb(var(--color-border))', borderRadius: 10, background: 'rgb(var(--color-surface))' }}>
          {isSelf ? (
            <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgb(var(--color-warning) / 0.08)', border: '1px solid rgb(var(--color-warning) / 0.25)', fontSize: 12, color: 'rgb(var(--color-text))' }}>
              You created this bonus pool and cannot approve it (NN-10).
              A different comp admin must approve.
            </div>
          ) : (
            <>
              <p style={{ fontSize: 13, margin: '0 0 12px', color: 'rgb(var(--color-text))' }}>
                Approve this bonus pool. Once approved, manager nominations can be submitted
                and the pool will be included in calculation runs.
              </p>
              {approveError && (
                <div className="modifier-manager-error-state" style={{ marginBottom: 12 }}>
                  <p style={{ margin: 0 }}>{approveError}</p>
                </div>
              )}
              <button type="button" className="primary-button"
                onClick={handleApprove} disabled={approving}>
                {approving ? 'Approving…' : 'Approve bonus pool (CA-2)'}
              </button>
            </>
          )}
        </div>
      )}

      {isApproved && (
        <div style={{ padding: '10px 14px', borderRadius: 8, background: 'rgb(var(--color-success) / 0.07)', border: '1px solid rgb(var(--color-success) / 0.2)', fontSize: 12, color: 'rgb(var(--color-success))' }}>
          ✓ Approved. Pool is active — allocations can be nominated and the engine
          will include this pool in calculation runs.
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// TAB 5 — ACTIVITY
// §30.1 GET /audit-logs entity_type=bonus_pool entity_id=selectedPoolId (uuid)
// Response: { data: [...], pagination: { next_cursor, has_more, limit } }
// Cursor pagination via ISO timestamp cursor param.
// Event labels sourced from p4_bonus_pool_router.py _audit() call sites.
// ===========================================================================
function ActivityPanel({ selectedPool }) {
  const [entries, setEntries]   = useState([]);
  const [loading, setLoading]   = useState(false);
  const [hasMore, setHasMore]   = useState(false);
  const [cursor, setCursor]     = useState(null);
  const [search, setSearch]     = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const PAGE_SIZE = 25;
  const mountedRef = useRef(true);

  const poolId = selectedPool?.bonus_pool_id;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    setEntries([]);
    setCursor(null);
  }, [poolId, refreshKey]);

  useEffect(() => {
    if (!poolId) return;
    setLoading(true);
    const params = new URLSearchParams({
      entity_type: 'bonus_pool',
      entity_id:   poolId,
      limit:       String(PAGE_SIZE),
    });
    if (cursor) params.set('cursor', cursor);

    apiFetch(`${API_BASE}/audit-logs?${params}`)
      .then(d => {
        if (!mountedRef.current) return;
        const newEntries = d.data ?? [];
        setEntries(prev => cursor ? [...prev, ...newEntries] : newEntries);
        setHasMore(d.pagination?.has_more ?? false);
      })
      .catch(() => { if (mountedRef.current) setEntries([]); })
      .finally(() => { if (mountedRef.current) setLoading(false); });
  }, [poolId, cursor, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  function loadMore() {
    if (entries.length > 0) setCursor(entries[entries.length - 1].created_at);
  }

  function refresh() {
    setEntries([]);
    setCursor(null);
    setRefreshKey(k => k + 1);
  }

  // Event label map — sourced from p4_bonus_pool_router.py _audit() call sites
  function eventLabel(eventType) {
    const map = {
      'p4.bonus_pool.created':              'Bonus pool created',
      'p4.bonus_pool.approved':             'Bonus pool approved (CA-2)',
      'p4.bonus_pool_allocation.created':   'Allocation nominated',
      'p4.bonus_pool_allocation.approved':  'Allocation approved',
    };
    return map[eventType] ?? eventType;
  }

  const filtered = search
    ? entries.filter(e =>
        (e.event_type ?? '').toLowerCase().includes(search.toLowerCase()) ||
        eventLabel(e.event_type).toLowerCase().includes(search.toLowerCase()) ||
        (e.change_summary ?? '').toLowerCase().includes(search.toLowerCase())
      )
    : entries;

  if (!poolId) {
    return (
      <div className="modifier-manager-empty-state">
        <h4>No pool selected</h4>
        <p>Select a bonus pool from the Overview tab to view activity.</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <span style={{ fontSize: 14, fontWeight: 700, color: 'rgb(var(--color-text))' }}>
            Activity — {selectedPool?.pool_name ?? 'Bonus Pool'}
          </span>
          <span style={{ marginLeft: 8, fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>
            {String(poolId).slice(0, 8)}…
          </span>
        </div>
        <button type="button" className="icon-button" onClick={refresh} title="Refresh" aria-label="Refresh activity">↻</button>
      </div>

      <input
        style={{ border: '1px solid rgb(var(--color-border))', borderRadius: 6, padding: '5px 10px', fontSize: 12, background: 'rgb(var(--color-surface))', color: 'rgb(var(--color-text))', width: '100%', boxSizing: 'border-box' }}
        placeholder="Filter activity…"
        value={search}
        onChange={e => setSearch(e.target.value)}
      />

      {loading && entries.length === 0 && (
        <div className="modifier-manager-loading">Loading activity…</div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="modifier-manager-empty-state">
          <h4>No activity yet</h4>
          <p>Actions taken on this bonus pool will appear here.</p>
        </div>
      )}

      {filtered.map((e, i) => (
        <div key={e.audit_log_id ?? i}
          style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid rgb(var(--color-border))' }}>
          <div style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))', minWidth: 140, flexShrink: 0 }}>
            {_iso(e.created_at)}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
            <span style={{ fontSize: 13, color: 'rgb(var(--color-text))' }}>{eventLabel(e.event_type)}</span>
            {e.change_summary && (
              <span style={{ fontSize: 11, color: 'rgb(var(--color-text-muted))' }}>{e.change_summary}</span>
            )}
            {e.user_id && (
              <span style={{ fontSize: 10, color: 'rgb(var(--color-text-muted))', fontFamily: 'ui-monospace,monospace' }}>
                by {String(e.user_id).slice(0, 8)}…
              </span>
            )}
          </div>
        </div>
      ))}

      {hasMore && (
        <div style={{ paddingTop: 8 }}>
          <button type="button" className="secondary-button" disabled={loading} onClick={loadMore}>
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}

// ===========================================================================
// MAIN — BonusPoolWorkspace (C-68)
// ===========================================================================
export default function BonusPoolWorkspace({ fiscalPeriodId, currentUser, onClose, onSaved }) {
  const [activeTab, setActiveTab]     = useState('overview');

  // Selected pool — set from Overview row click or post-create
  // Stored as full pool object so Approval/Activity/Allocations tabs
  // have pool fields (including created_by from d136e06 patch) without
  // a separate fetch.
  const [selectedPool, setSelectedPool] = useState(null);
  const [pools, setPools]               = useState([]);

  // When a pool is selected from Overview, switch to Allocations tab
  function handleSelectPool(poolId, currentPools) {
    const poolList = currentPools ?? pools;
    const found = poolList.find(p => p.bonus_pool_id === poolId) ?? null;
    setSelectedPool(found);
    setActiveTab('allocations');
  }

  // Pending approval indicator for Approval tab badge
  const needsApproval = selectedPool && !selectedPool.approved_by;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, fontFamily: '"IBM Plex Sans","Segoe UI",Arial,sans-serif', color: 'rgb(var(--color-text))' }}>

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '16px 0 12px', borderBottom: '1px solid rgb(var(--color-border))', marginBottom: 0 }}>
        <div>
          <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgb(var(--color-text-muted))', margin: '0 0 4px' }}>
            EXECUTE · Programs &amp; Adjustments · Bonus Pools
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{ fontSize: 20, fontWeight: 700, color: 'rgb(var(--color-text))', margin: 0 }}>
              {selectedPool ? selectedPool.pool_name : 'Bonus Pools'}
            </h1>
            {selectedPool?.pool_code && (
              <code className="modifier-manager-code">{selectedPool.pool_code}</code>
            )}
            <span className="modifier-manager-badge modifier-manager-badge--readonly">Enterprise</span>
            {selectedPool && (
              <span className={`modifier-manager-badge ${POOL_APPROVAL_BADGE(selectedPool).cls}`}>
                {POOL_APPROVAL_BADGE(selectedPool).label}
              </span>
            )}
          </div>
          <p style={{ fontSize: 12, color: 'rgb(var(--color-text-muted))', margin: '4px 0 0' }}>
            {selectedPool
              ? `${ALLOCATION_METHOD_LABELS[selectedPool.allocation_method]} · ${ELIGIBILITY_LABELS[selectedPool.eligibility_type]} · ${_fmt(selectedPool.pool_amount)} ${selectedPool.pool_currency_code ?? ''}`
              : `Discretionary pool allocation programs.${fiscalPeriodId ? ` · Period: ${fiscalPeriodId}` : ''}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {selectedPool && (
            <button type="button" className="secondary-button"
              onClick={() => { setSelectedPool(null); setActiveTab('overview'); }}>
              ← All Pools
            </button>
          )}
          <button type="button" className="secondary-button" onClick={onClose}>← Back</button>
        </div>
      </div>

      {/* ── Tab bar ────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid rgb(var(--color-border))', marginBottom: 20 }}>
        {TABS.map(tab => (
          <button
            key={tab.key}
            type="button"
            onClick={() => setActiveTab(tab.key)}
            style={{
              padding: '10px 18px', fontSize: 13,
              fontWeight: activeTab === tab.key ? 700 : 500,
              color: activeTab === tab.key ? 'rgb(var(--color-primary))' : 'rgb(var(--color-text-muted))',
              background: 'none', border: 'none',
              borderBottom: activeTab === tab.key ? '2px solid rgb(var(--color-primary))' : '2px solid transparent',
              cursor: 'pointer', transition: 'color 0.15s, border-color 0.15s',
              marginBottom: -1,
            }}
          >
            {tab.label}
            {/* Pending approval attention indicator */}
            {tab.key === 'approval' && needsApproval && (
              <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 700, padding: '1px 5px', borderRadius: 10, background: 'rgb(var(--color-warning) / 0.15)', color: 'rgb(var(--color-warning))' }}>!</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tab content ────────────────────────────────────────────────── */}
      <div style={{ minHeight: 400 }}>

        {activeTab === 'overview' && (
          <OverviewPanel
            fiscalPeriodId={fiscalPeriodId}
            onSelectPool={handleSelectPool}
            selectedPoolId={selectedPool?.bonus_pool_id}
          />
        )}

        {activeTab === 'allocations' && (
          <AllocationsPanel
            selectedPool={selectedPool}
            currentUser={currentUser}
          />
        )}

        {activeTab === 'results' && (
          <ResultsPanel
            selectedPool={selectedPool}
          />
        )}

        {activeTab === 'approval' && (
          <ApprovalPanel
            selectedPool={selectedPool}
            currentUser={currentUser}
            onActionComplete={() => {
              // Refresh pool list and update selectedPool with new approved_by
              // Re-fetch by reloading overview — simplest pattern
              onSaved?.();
            }}
          />
        )}

        {activeTab === 'activity' && (
          <ActivityPanel
            selectedPool={selectedPool}
          />
        )}

      </div>
    </div>
  );
}
